import { BrowserRouter, Routes, Route } from "react-router-dom";
import { Toaster } from "sonner";
import PdfPreviewHost from "./components/PdfPreview";
import Layout from "./components/Layout";
import PinLock from "./components/PinLock";
import Dashboard from "./pages/Dashboard";
import Clients from "./pages/Clients";
import ClientDetail from "./pages/ClientDetail";
import ClientProgress from "./pages/ClientProgress";
import ScoreSimulator from "./pages/ScoreSimulator";
import ScoreSimulatorNew from "./pages/ScoreSimulatorNew";
import ScoreSimulatorEnhanced from "./pages/ScoreSimulatorEnhanced";
import ShareProgress from "./pages/ShareProgress";
import ClientPortal from "./pages/ClientPortal";
import ImportReport from "./pages/ImportReport";
import Letters from "./pages/Letters";
import CardRecommendations from "./pages/CardRecommendations";
import DisputeWizard from "./pages/DisputeWizard";
import PreScreening from "./pages/PreScreening";
import UpdateClientProgress from "./pages/UpdateClientProgress";
import Payments from "./pages/Payments";
import JustMailPage from "./pages/JustMailPage";
import FTCBuilderPage from "./pages/FTCBuilderPage";
import PaydaySTPage from "./pages/PaystubWizardPage";
import SportsBettingPage from "./pages/SportsBettingPage";
import SportsPicksPage from './pages/SportsPicksPage';
import TicketReviewPage from './pages/TicketReviewPage';
import TodaysPicksPage from './pages/TodaysPicksPage';
import PublicPicksPage from './pages/PublicPicksPage';
import ResultsPage from './pages/ResultsPage';
import NbaPicksPage from './pages/NbaPicksPage';
import LedgerPage from './pages/LedgerPage';
import SchedulePage from './pages/SchedulePage';
import BankrollPage from './pages/BankrollPage';
import TrashPage from "./pages/TrashPage";
import ClientIntakePage from "./pages/ClientIntakePage";
import PaystubIntakePage from "./pages/PaystubIntakePage";
import SignContractPage from "./pages/SignContractPage";
import PDFFinalizePage from "./pages/PDFFinalizePage";
import CopyFaxConverterPage from "./pages/CopyFaxConverterPage";
import IDConverterPage from "./pages/IDConverterPage";
import ActivityLogPage from "./pages/ActivityLogPage";
import SharedReportPage from "./pages/SharedReportPage";
import AnalyzeReportPage from "./pages/AnalyzeReportPage";
import SendIntakePage from "./pages/SendIntakePage";
import SharedProgressPage from "./pages/SharedProgressPage";
import ClientProgressPage from "./pages/ClientProgressPage";
import CreditRepairStepsPage from "./pages/CreditRepairStepsPage";
import TradelineManagerPage from "./pages/TradelineManagerPage";
import TradelineWorldPage from "./pages/TradelineWorldPage";
import CreditBuilderListPage from "./pages/CreditBuilderListPage";
import MomsHousePage from "./pages/MomsHousePage";
import MonthlyBillsPage from "./pages/MonthlyBillsPage";
import ApartmentLeasePage from "./pages/ApartmentLeasePage";
import EmailRemindersPage from "./pages/EmailRemindersPage";
// 2026-02-24 — To-Do List page removed from the app (user request).
// The daily 9AM PST "Credit To-Do" reminder emails couldn't be reliably
// silenced from the UI, so the entire section is pulled.  The
// ToDoListPage component file + /api/todos backend still exist so
// legacy data isn't lost — they're just no longer wired to any route or
// nav element.
// import ToDoListPage from "./pages/ToDoListPage";
import PersonalTabViewPage from "./pages/PersonalTabViewPage";
import ContractorInvoicesPage from "./pages/ContractorInvoicesPage";
import ContractorInvoiceShareView from "./pages/ContractorInvoiceShareView";
import PublicCreditBuilderListPage from "./pages/PublicCreditBuilderListPage";
import PublicTradelinesPage from "./pages/PublicTradelinesPage";
import HandwritingConverterPage from "./pages/HandwritingConverterPage";
import LetterIntelligencePage from "./pages/LetterIntelligencePage";
import ClientUpdatePage from "./pages/ClientUpdatePage";
import DisputeStatusGuidePage from "./pages/DisputeStatusGuidePage";
import ClientUpdatesPage from "./pages/ClientUpdatesPage";
import AccountSummaryBreakdownPage from "./pages/AccountSummaryBreakdownPage";
import DisputeLetterDemoPage from "./pages/DisputeLetterDemoPage";
import LeaseIntakePage from "./pages/LeaseIntakePage";

function App() {
  return (
    <BrowserRouter>
      <Toaster position="top-right" richColors />
      <PdfPreviewHost />
      <Routes>
        {/* PUBLIC ROUTES - No PIN required */}
        {/* Contract signing page - clients access this */}
        <Route path="/sign-contract/:contractId" element={<SignContractPage />} />
        
        {/* Shared credit analysis report - clients access this via shareable link */}
        <Route path="/shared-report/:shareId" element={<SharedReportPage />} />
        
        {/* Shared client progress report - clients access this via shareable link */}
        <Route path="/progress/:shareId" element={<SharedProgressPage />} />
        
        {/* Client intake form - clients fill this out, no PIN needed */}
        <Route path="/client-intake" element={<ClientIntakePage />} />
        <Route path="/paystub-intake" element={<PaystubIntakePage />} />
        <Route path="/paystub-intake/:token" element={<PaystubIntakePage />} />
        {/* 2026-09-28 — Public tenant-facing intake for the Lease flow */}
        <Route path="/lease-intake" element={<LeaseIntakePage />} />
        
        {/* Personal tab view - family members see their own tab only */}
        <Route path="/moms-house/tab/:shareId" element={<PersonalTabViewPage />} />
        
        {/* Public read-only contractor invoice share link */}
        <Route path="/contractor-invoice/:token" element={<ContractorInvoiceShareView />} />

        {/* Public-facing resources for marketing site (no PIN) */}
        <Route path="/public/credit-builder-list" element={<PublicCreditBuilderListPage />} />
        <Route path="/public/tradelines" element={<PublicTradelinesPage />} />
        <Route path="/nicks-sports/:token" element={<PublicPicksPage />} />

        {/* Public client status-update form — clients submit dispute progress here.
            Standalone page, no sidebar, no access to any other part of the app. */}
        <Route path="/client-update" element={<ClientUpdatePage />} />
        <Route path="/dispute-status" element={<DisputeStatusGuidePage />} />

        {/* ALL OTHER ROUTES - PIN PROTECTED */}
        <Route path="/*" element={
          <PinLock>
            <Routes>
              {/* Client portal */}
              <Route path="/portal/:clientId" element={<ClientPortal />} />
              
              {/* Dispute Wizard - standalone page */}
              <Route path="/dispute-wizard/new" element={<Clients />} />
              <Route path="/dispute-wizard/:id" element={<DisputeWizard />} />
              
              {/* FTC Report Builder - standalone page */}
              <Route path="/ftc-builder" element={<FTCBuilderPage />} />
              <Route path="/ftc-builder/:id" element={<FTCBuilderPage />} />

              {/* Dispute Letter demo/preview */}
              <Route path="/dispute-letter-demo" element={<DisputeLetterDemoPage />} />
              
              {/* Payday ST - standalone page */}
              <Route path="/payday-st" element={<PaydaySTPage />} />
              <Route path="/paystub-wizard" element={<PaydaySTPage />} />
              <Route path="/sports-betting" element={<SportsBettingPage />} />
              <Route path="/sports-betting/ai-picks" element={<TodaysPicksPage />} />
              <Route path="/sports-betting/results" element={<ResultsPage />} />
              <Route path="/sports-betting/nba-picks" element={<NbaPicksPage />} />
              <Route path="/sports-betting/bankroll" element={<BankrollPage />} />
              <Route path="/sports-betting/review" element={<TicketReviewPage />} />
              <Route path="/sports-betting/ledger" element={<LedgerPage />} />
              <Route path="/sports-betting/schedule" element={<SchedulePage />} />
              <Route path="/sports-betting/:league" element={<SportsPicksPage />} />
              
              {/* Pre-Screening - standalone page */}
              <Route path="/pre-screening" element={<PreScreening />} />
              
              {/* Just Mail - standalone mailing without wizard */}
              <Route path="/just-mail" element={<JustMailPage />} />
              <Route path="/just-mail/:id" element={<JustMailPage />} />
              
              {/* PDF Finalize - Flatten and export PDFs */}
              <Route path="/pdf-finalize" element={<PDFFinalizePage />} />
              <Route path="/account-summary-breakdown" element={<AccountSummaryBreakdownPage />} />

              {/* Copy & Fax Converter — make PDFs look like a scan or fax */}
              <Route path="/copy-fax" element={<CopyFaxConverterPage />} />
              <Route path="/id-converter" element={<IDConverterPage />} />
              <Route path="/activity" element={<ActivityLogPage />} />

              {/* Handwritten Letter Converter — standalone entry so users
                  don't have to go through the FTC builder to convert a PDF
                  to a handwritten letter. */}
              <Route path="/handwriting-converter" element={<HandwritingConverterPage />} />
              <Route path="/letter-intelligence" element={<LetterIntelligencePage />} />
              
              {/* Analyze Report - Credit report analysis */}
              <Route path="/analyze-report" element={<AnalyzeReportPage />} />
              
              {/* Credit Repair Steps - Send repair instructions without FTC report */}
              <Route path="/credit-repair-steps" element={<CreditRepairStepsPage />} />
              
              {/* Tradeline Manager - Manage and send tradeline lists */}
              <Route path="/tradeline-manager" element={<TradelineManagerPage />} />

              {/* Tradeline World - Pipeline + Active tracker with 90-day removal emails */}
              <Route path="/tradeline-world" element={<TradelineWorldPage />} />
              
              {/* Credit Builder List - Complete list of credit building services */}
              <Route path="/credit-builder-list" element={<CreditBuilderListPage />} />
              <Route path="/apartment-lease" element={<ApartmentLeasePage />} />
              <Route path="/email-reminders" element={<EmailRemindersPage />} />
              
              {/* Mom's House - Renovation expense tracker */}
              <Route path="/moms-house" element={<MomsHousePage />} />
              <Route path="/monthly-bills" element={<MonthlyBillsPage />} />
              {/* /todo route retired 2026-02-24 (ToDoListPage.jsx deleted) */}
              <Route path="/contractor-invoices" element={<ContractorInvoicesPage />} />
              
              {/* Send Intake Link - Share link with clients */}
              <Route path="/send-intake" element={<SendIntakePage />} />

              {/* Client Updates - Inbox for status updates submitted via the public link */}
              <Route path="/client-updates" element={<ClientUpdatesPage />} />

              {/* Client Progress Tool - Analyze Credit Karma/Experian Screenshots */}
              <Route path="/client-progress" element={<ClientProgressPage />} />
              
              {/* Update Client Progress - standalone page */}
              <Route path="/update-progress/:id" element={<UpdateClientProgress />} />
              
              {/* Admin routes with layout */}
              <Route element={<Layout />}>
                <Route path="/" element={<Dashboard />} />
                <Route path="/clients" element={<Clients />} />
                <Route path="/clients/:id" element={<ClientDetail />} />
                <Route path="/clients/:id/progress" element={<ClientProgress />} />
                <Route path="/score-simulator" element={<ScoreSimulator />} />
                <Route path="/score-simulator-new" element={<ScoreSimulatorNew />} />
                <Route path="/score-simulator-enhanced" element={<ScoreSimulatorEnhanced />} />
                <Route path="/share/:clientId" element={<ShareProgress />} />
                <Route path="/import-report" element={<ImportReport />} />
                <Route path="/letters" element={<Letters />} />
                <Route path="/card-recommendations" element={<CardRecommendations />} />
                <Route path="/payments" element={<Payments />} />
                <Route path="/trash" element={<TrashPage />} />
              </Route>
            </Routes>
          </PinLock>
        } />
      </Routes>
    </BrowserRouter>
  );
}

export default App;
